import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SelectModule } from '@openng/optimus-ui/select';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IPropertyDestination } from '../../../models/property/property.interface';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { environment } from '../../../../environments/environment';
import { getBrands } from '../../../models/property/property-brands.enum';
import { MatButton } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { PropertyService } from '../../../services/property.service';
import { Subject, takeUntil } from 'rxjs';
import { PropertyEditDto, UniqueSellingPoint } from '../../../models/property/propertyEditDto.interface';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { DialogModule } from '@openng/optimus-ui/dialog';

@Component({
    selector: 'app-property',
    imports: [MatDialogModule, ReactiveFormsModule, SelectModule, CheckboxModule, MatButton, CommonModule, TooltipModule, InputTextModule, DialogModule],
    templateUrl: './property.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./property.component.scss']
})
export class PropertyComponent implements OnInit {
  propertyForm!: FormGroup;

  brandOptions: { label: string; value: string }[] = [];
  starsOptions: { label: string; value: string }[] = [];
  numberOfStarsOptions: { label: string; value: string }[] = [];

  typeOptions: { label: string; value: string }[] = [];

  destinationOptions: { label: string; value: string }[] = [];

  private _destroy$ = new Subject<void>();
  langs: string[] = ['hr', 'si', 'de', 'it', 'en', 'fr', 'cs', 'pl', 'sk', 'hu'];

  section: { name: string; open: boolean }[] = [
    {name: "usp", open: false},
    {name: "urls", open: false},
    {name: "bookingUrls", open: false},
  ]

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PropertyComponent>,
    @Inject(MAT_DIALOG_DATA) public property: PropertyEditDto,
    private propertyService: PropertyService,
  ) {
  }

  ngOnInit(): void {
    //this.setProperty()
    this.setBrandOptions();
    this.setStarsOptions();
    this.setNumberOfStarsOptions();
    this.setTypeOptions();
    this.setDestinationOptions()
    this.setFormData()
  }

  onSave(): void {
    if (this.propertyForm.valid) {
      const payload = {...this.propertyForm.value, crmPropertyId: this.property?.crmPropertyId};
      this.propertyService.updateProperty(payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response: any) => {
            this.dialogRef.close(response ?? true);
          },
          error: (error: any) => {
            console.error("Error loading destinations" + error);
          }
        })
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  setBrandOptions() {
    const brandOptions = getBrands(environment.envName);

    this.brandOptions = Object.keys(brandOptions).map(key => ({
      label: brandOptions[key as keyof typeof brandOptions],
      value: brandOptions[key as keyof typeof brandOptions]
    }))
    this.brandOptions.push({label: 'Non branded', value: ''},
    )
  }

  setStarsOptions() {
    this.starsOptions = [
      {label: 'Non rate', value: ''},
      {label: '2*', value: '2*'},
      {label: '3*', value: '3*'},
      {label: '3*/4*', value: '3*/4*'},
      {label: '4*', value: '4*'},
      {label: '4/3*', value: '4/3*'},
      {label: '4*/5*', value: '4*/5*'},
      {label: '4*S', value: '4*S'},
      {label: '5*', value: '5*'},
    ]
  }

  setNumberOfStarsOptions() {
    this.numberOfStarsOptions = [
      {label: 'Non rate', value: ''},
      {label: '2', value: '2'},
      {label: '3', value: '3'},
      {label: '4', value: '4'},
      {label: '5', value: '5'},
    ]
  }

  setTypeOptions() {
    this.typeOptions = [
      {label: 'Parcele', value: 'Parcele'},
      {label: 'Sobe i suiteovi', value: 'Sobe i suiteovi'},
      {label: 'Glamping šatori', value: 'Glamping šatori'},
      {label: 'Apartmani i sobe', value: 'Apartmani i sobe'},
      {label: 'Camping homeovi', value: 'Camping homeovi'}
    ];
  }

  private setDestinationOptions() {
    this.propertyService.getDestinationsList()
      .pipe(takeUntil(this._destroy$))
      .subscribe({

        next: (response: any) => {
          this.destinationOptions = response.map((dest: IPropertyDestination) => ({
            label: dest.name,
            value: dest.id,
          }));
        },
        error: (error: any) => {
          console.error("Error loading destinations" + error);
        }
      })
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  setFormData() {
    console.log("This prop::", this.property);
    this.propertyForm = this.fb.group({
      destinationId: [this.property?.destinationId],
      // destination: this.fb.group({
      //   id: [this.property?.destination?.id || '', []],
      //   name: [this.property.destination?.name || '', []],
      //   group: [this.property.destination?.group || '', []],
      //   sector: [this.property.destination?.sector || '', []]
      // }),

      urls: this.fb.group({
        hr: [this.property?.urls?.['hr'] || '', []],
        si: [this.property?.urls?.['si'] || '', []],
        de: [this.property?.urls?.['de'] || '', []],
        it: [this.property?.urls?.['it'] || '', []],
        en: [this.property?.urls?.['en'] || '', []],
        fr: [this.property?.urls?.['fr'] || '', []],
        cs: [this.property?.urls?.['cs'] || '', []],
        pl: [this.property?.urls?.['pl'] || '', []],
        sk: [this.property?.urls?.['sk'] || '', []],
        hu: [this.property?.urls?.['hu'] || '', []],
      }),

      bookingUrls: this.fb.group({
        hr: [this.property?.bookingUrls?.['hr'] || '', []],
        si: [this.property?.bookingUrls?.['si'] || '', []],
        de: [this.property?.bookingUrls?.['de'] || '', []],
        it: [this.property?.bookingUrls?.['it'] || '', []],
        en: [this.property?.bookingUrls?.['en'] || '', []],
        fr: [this.property?.bookingUrls?.['fr'] || '', []],
        cs: [this.property?.bookingUrls?.['cs'] || '', []],
        pl: [this.property?.bookingUrls?.['pl'] || '', []],
        sk: [this.property?.bookingUrls?.['sk'] || '', []],
        hu: [this.property?.bookingUrls?.['hu'] || '', []],
      }),
      name: [this.property?.name || '', []],
      isActive: [this.property?.isActive || false, []],

      brand: [this.property?.brand || '', []],
      type: [this.property?.type || '', []],
      stars: [this.property?.stars || '', []],
      numberOfStars: [this.property?.numberOfStars || '', []],

      minPrice: [this.property?.minPrice || '', []],
      imageUrl: [this.property?.imageUrl || '', []],
      mapUrl: [this.property?.mapUrl || '', []],

      uniqueSellingPoints: this.fb.group({
        hr: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.hr)),
        si: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.si)),
        de: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.de)),
        it: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.it)),
        en: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.en)),
        fr: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.fr)),
        cs: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.cs)),
        pl: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.pl)),
        sk: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.sk)),
        hu: this.fb.array(this.createUspArray(this.property?.uniqueSellingPoints?.hu)),
      }),

      family: [this.property?.family || false, []],
      couples: [this.property?.couples || false, []],
      generic: [this.property?.generic || false, []],
      highSpenders: [this.property?.highSpenders || false, []],
      lowSpenders: [this.property?.lowSpenders || false, []],
      pet: [this.property?.pet || false, []],
      bike: [this.property?.bike || false, []],
      alpine: [this.property?.alpine || false, []],
      naturisti: [this.property?.naturisti || false, []],
    });
  }

  createUspArray(usps: UniqueSellingPoint[] = []): FormGroup[] {
    return (usps || []).map(u =>
      this.fb.group({
        title: [u.title || '', []],
        order: [u.order || 0, []]
      })
    );
  }

  getUspArray(lang: string): FormArray {
    return this.propertyForm.get(['uniqueSellingPoints', lang]) as FormArray;
  }

  toggleSection(sectionName: string) {
    const section = this.section.find(s => s.name === sectionName);
    if (section) {
      section.open = !section.open;
    }
  }

  getSectionStatus(sectionName: string): boolean {
    const section = this.section.find(s => s.name === sectionName);
    return section ? section.open : false;
  }
}
